import config from 'config';

import { SortOptionsHumanToSnakeMapping } from '~/builder/components/BuilderConstructorGrid';
import { LOCALIZED_LABELS } from '~/searchCio/constants/cioConstants';
import { ProductTag } from '~/shared/constants/products';
import { getBrandValue } from '~/shared/utils/brandNameHelper';
import generateImageServiceImageUrl from '~/shared/utils/generateImageServiceImageUrl';
import logger from '~/shared/utils/logger';
import { MEMBERSHIP_BRAND_ID_ENUMS } from '~/shared/utils/membershipBrandIdEnums';
import { sizeByKey } from '~/shared/utils/sizeConstants';

import getAutoAppliedInitialFilters from './getAutoAppliedInitialFilters';
import getExclusionFpls from './getExclusionFpls';

const debug = logger.extend('cioUtils');

// the number of additional facets we should include other than color and size
export const MAX_FACET_COUNT = 7;

export const SIZE_PREFIX = 'size_';

export const getAllSizeFilters = () =>
  getBrandValue({
    savage: [
      'size_bottom',
      'size_bra',
      'size_bralette',
      'size_men',
      'size_sport',
      'size_lingerie',
    ],
    default: ['size_top', 'size_bottom', 'size_bra', 'size_shoes'],
  });

export const getMembershipExclusionFilters = ({ isElite, isVip }) => {
  let membershipExclusionFilters;

  if (isElite) {
    membershipExclusionFilters = ['lead_only_flag'];
  } else if (isVip) {
    membershipExclusionFilters = ['lead_only_flag', 'elite_only_flag'];
  } else {
    membershipExclusionFilters = ['vip_only_flag'];
  }

  return membershipExclusionFilters.map((field) => ({
    name: field,
    value: 'False',
  }));
};

/**
 * Separates the size filters from the rest of the filters. We need this primarily for
 * moving the size filters from the `filters` param to the `prefilterExpressions` param
 * in the CIO API.
 *
 * @param {Object} filters - The filters object containing all the filters with shape { color: ['red'], size_top: ['X-Small'] }.
 * @returns {Object} - An object containing the size filters and the rest of the filters.
 */
export const isolateSizeFilters = (filters) => {
  const sizeFilters = {};
  const nonSizeFilters = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (key.startsWith(SIZE_PREFIX)) {
      sizeFilters[key] = value;
    } else {
      nonSizeFilters[key] = value;
    }
  });

  return { sizeFilters, nonSizeFilters };
};

/**
 * Case 1: Partitioned Selected Sizes
 * Arrays are not supported in CIO pre-filter expressions for non-range based filters
 * We need to partition the size filters into separate filter expressions based on the field.
 *
 * @example
 * Input: selectedSizeFilters = { size_top: ['X-Small', 'Small'], size_bottom: ['X-Small'] }
 * Output: [{ name: 'available_size_top', value: 'X-Small' }, { name: 'available_size_top', value: 'Small' }, { name: 'available_size_bottom', value: 'X-Small' }]
 *
 * @param {Object} selectedSizeFilters - An object of currently selected size filters with the shape { size_top: ['X-Small', 'Small'], size_bottom: ['X-Small'] }.
 * @returns {Array} - An array of filter expressions for the given size filters.
 * */
export const getPartitionedSizeFilterExpressions = (selectedSizeFilters) => {
  const sizeFilterExpressions = [];
  Object.entries(selectedSizeFilters).forEach(([key, value]) => {
    if (!Array.isArray(value) || value.length === 0) {
      return;
    }

    value.forEach((val) => {
      sizeFilterExpressions.push({
        name: urlFiltersToCioFacets[key] || key,
        value: val,
      });
    });
  });

  return sizeFilterExpressions;
};

/**
 * Case 2: Wildcard sizes
 * Return all sizes (by using the wildcard *) for products that do not have any
 * size associations with the selected size type. Explicitly exclude products with
 * the value 'no-size' for the selected size type, since the wildcard * will include
 * products with no size associations.
 *
 * @example
 * Input: selectedSizeFilters = { size_top: ['X-Small', 'Small'], size_bottom: ['X-Small'] }
 * Output: [
 *  { and: [{ name: 'available_size_bra', value: '*' }, { not: { name: 'available_size_bra', value: 'no-size' } }],
 *  { and: [{ name: 'available_size_shoes', value: '*' }, { not: { name: 'available_size_shoes', value: 'no-size' } }]
 * ]
 *
 * @param {Object} selectedSizeFilters - An object of currently selected size filters with the shape { size_top: ['X-Small', 'Small'], size_bottom: ['X-Small'] }.
 * @returns {Array} - An array of filter expressions for the non-selected size filters.
 * */
export const getWildcardSizeFilterExpressions = (selectedSizeFilters) => {
  const sizeFilterExpressions = [];
  const sizeFilters = getAllSizeFilters();
  const nonSelectedSizeFilters = sizeFilters.filter(
    (sizeFilter) =>
      !selectedSizeFilters?.[sizeFilter] ||
      selectedSizeFilters[sizeFilter].length === 0
  );

  nonSelectedSizeFilters.forEach((field) => {
    sizeFilterExpressions.push({
      and: [
        { name: urlFiltersToCioFacets[field] || field, value: '*' },
        {
          not: {
            name: urlFiltersToCioFacets[field] || field,
            value: 'no-size',
          },
        },
      ],
    });
  });

  return sizeFilterExpressions;
};

/**
 * Case 3: Accessories sizes
 * Return all products that do not have any size associations. These products will have
 * the value 'no-size' for all size fields, configured by CIO.
 *
 * @returns {Array} - An array of filter expressions for products without size associations.
 * */
export const getAccessoriesSizeFilterExpressions = () => {
  const sizeFilterExpressions = [];
  const sizeFilters = getAllSizeFilters();
  sizeFilterExpressions.push({
    and: sizeFilters.map((field) => ({
      name: urlFiltersToCioFacets[field] || field,
      value: 'no-size',
    })),
  });

  return sizeFilterExpressions;
};

/**
 * Generates filter expressions using OR logic based on the selected size filters.
 * @param {Object} selectedSizeFilters - An object of currently selected size filters with the shape { size_top: ['X-Small', 'Small'], size_bottom: ['X-Small'] }.
 * @returns {Array} - An array of filter expressions for the given size filters.
 */
export const getSizeFilterExpression = ({ selectedSizeFilters = {} }) => {
  const sizeFilterExpressions = [
    ...getPartitionedSizeFilterExpressions(selectedSizeFilters),
    ...getWildcardSizeFilterExpressions(selectedSizeFilters),
    ...getAccessoriesSizeFilterExpressions(),
  ];

  return sizeFilterExpressions?.length > 0
    ? [{ or: sizeFilterExpressions }]
    : [];
};

/**
 * Generates pre-filter expressions for the given parameters, focusing on hidden facets.
 *
 * IMPORTANT: we need to include `sizeFilters` here because we need to filter different
 * size types using OR statements. The normal `filters` param in CIO APIs by default
 * will only filter using AND statements. This is an issue because applying multiple
 * size types with AND statements will return a very limited set of products. Using OR
 * based statements is fundamental to the way our business filter sizes.
 *
 * @param {Object} options
 * @param {boolean} options.isElite - Indicates if the user is an elite member.
 * @param {boolean=} options.isVip - Indicates if the user is a VIP member.
 * @param {number=} options.dateNowInMs - The current date in milliseconds.
 * @param {boolean=} options.shouldShowWarehouseProducts - Controls whether warehouse products are included in the autosuggest.
 * @param {boolean=} options.shouldHideOriginRestricted - Controls whether products tagged as origin-restricted should be hidden
 * @param {any=} options.selectedSizeFilters - An object of currently selected size filters with the shape { size_top: ['X-Small', 'Small'], size_bottom: ['X-Small'] }.
 * @param {array=} options.excludeTagIds - An array of tag ids used for product exclusion.
 * @param {array=} options.excludeFplCodes - An array of FPL global codes used for product exclusion.
 * @returns {Object} - A pre-filter expression object for the given parameters.
 */
export const getPreFilterExpressions = ({
  isElite,
  isVip,
  dateNowInMs,
  shouldShowWarehouseProducts = false,
  shouldHideOriginRestricted = false,
  selectedSizeFilters = {},
  excludeTagIds = [],
  excludeFplCodes = [],
  ...rest
}) => {
  const useSavageCIOFilters = config.get(
    'public.brand.features.useSavageCIOFilters'
  );
  if (useSavageCIOFilters) {
    return getSXFPreFilterExpressions({
      isVip,
      dateNowInMs,
      selectedSizeFilters,
      shouldHideOriginRestricted,
      excludeTagIds,
      excludeFplCodes,
      ...rest,
    });
  }

  return getFLPreFilterExpressions({
    isElite,
    isVip,
    dateNowInMs,
    shouldShowWarehouseProducts,
    shouldHideOriginRestricted,
    selectedSizeFilters,
    excludeTagIds,
    excludeFplCodes,
  });
};

/**
 * @param {string} key - The key for the filter expression.
 * @param {(number|boolean)[]} [values=[]] - An array of values to be excluded from the filter expression.
 */
export const createExclusionPreFilterExpression = (key, values) => {
  return values.map((value) => {
    let valueString = value.toString();

    if (typeof value === 'boolean') {
      valueString = value ? 'True' : 'False';
    }

    return {
      not: {
        name: key,
        value: valueString,
      },
    };
  });
};

// TODO: Is SXF getting support for predefined preFilter expressions or do we stick with
// filters like `featured_product_location_id_list` and `tag_id_list`?
/**
 * Generates pre-filter expressions for SXF products.
 *
 * @param {Object} options - The parameters for generating pre-filter expressions.
 * @param {boolean} options.isVip - Indicates if the user is a VIP member.
 * @param {number} options.dateNowInMs - The current date in milliseconds, used to filter out products that are not expected to be available
 * @param {Object} options.selectedSizeFilters - The selected size filters.
 * @param {boolean=} options.shouldHideOriginRestricted - Controls whether products tagged as origin-restricted should be hidden
 * @param {boolean} [options.includeFplOnly=false] - Whether to include FPL only products.
 * @param {boolean} [options.includeCollectible=false] - Whether to include collectible products.
 * @param {boolean} [options.autoExcludeClearanceFplIds=true] - Whether to automatically exclude clearance FPL IDs.
 * @param {boolean} [options.isSearch=false] - Indicates if the context is a search, when search most of the exclusion filters are not needed
 * @param {string} [options.tld='.com'] - The top-level domain.
 * @param {array=} options.excludeTagIds - An array of tag ids used for product exclusion.
 * @param {array=} options.excludeFplCodes - An array of FPL global codes used for product exclusion.
 * @returns {Object} The pre-filter expressions.
 */
const getSXFPreFilterExpressions = ({
  isVip,
  dateNowInMs,
  selectedSizeFilters,
  shouldHideOriginRestricted = false,
  includeFplOnly = false,
  includeCollectible = false,
  autoExcludeClearanceFplIds = true,
  isSearch = false,
  tld = '.com',
  excludeTagIds = [],
  excludeFplCodes = [],
}) => {
  const excludeFpls = getExclusionFpls({
    autoExcludeClearanceFplIds: !isSearch && autoExcludeClearanceFplIds,
    tld,
    isVip,
  });
  const excludeTagIdsSet = new Set([]);

  if (!isSearch) {
    if (!includeFplOnly) {
      excludeTagIdsSet.add(ProductTag.fplOnly);
    }

    if (!includeCollectible) {
      excludeTagIdsSet.add(ProductTag.collectible);
    }
  }

  if (isVip) {
    excludeTagIdsSet.add(ProductTag.leadOnly);
  } else {
    excludeTagIdsSet.add(ProductTag.vipOnly);
  }

  if (shouldHideOriginRestricted) {
    excludeTagIdsSet.add(ProductTag.removeOrigin);
  }

  excludeTagIds.forEach((tagId) => excludeTagIdsSet.add(tagId));

  const normalizedFplCodes = normalizeFplCodes(excludeFplCodes);

  return {
    and: [
      ...getSizeFilterExpression({ selectedSizeFilters }),
      ...(dateNowInMs
        ? [
            {
              name: 'date_expected_epoch_timestamp',
              range: ['-inf', dateNowInMs],
            },
          ]
        : []),
      ...createExclusionPreFilterExpression(
        'featured_product_location_id_list',
        excludeFpls
      ),
      ...createExclusionPreFilterExpression(
        'tag_id_list',
        Array.from(excludeTagIdsSet)
      ),
      ...createExclusionPreFilterExpression(
        'active_featured_product_location_code_list',
        normalizedFplCodes
      ),
    ],
  };
};

const getFLPreFilterExpressions = ({
  isElite,
  isVip,
  dateNowInMs,
  shouldShowWarehouseProducts = false,
  shouldHideOriginRestricted = false,
  selectedSizeFilters = {},
  excludeTagIds = [],
  excludeFplCodes = [],
}) => {
  const excludeTagIdsSet = new Set([
    ...(shouldHideOriginRestricted ? [ProductTag.removeOrigin] : []),
    ...excludeTagIds,
  ]);
  const normalizedFplCodes = normalizeFplCodes(excludeFplCodes);

  return {
    and: [
      ...getSizeFilterExpression({ selectedSizeFilters }),
      ...createExclusionPreFilterExpression(
        'tag_id_list',
        Array.from(excludeTagIdsSet)
      ),
      ...createExclusionPreFilterExpression(
        'active_featured_product_location_code_list',
        normalizedFplCodes
      ),
      ...(dateNowInMs
        ? [
            {
              name: 'date_expected_epoch_timestamp',
              range: ['-inf', dateNowInMs],
            },
          ]
        : []),
      {
        and: getMembershipExclusionFilters({ isElite, isVip }),
      },
      {
        name: 'is_warehouse_product',
        value: shouldShowWarehouseProducts ? 'True' : 'False',
      },
      /* This is hard coded to False because prefilter expression only returns products where the criteria is matched,
      for products where hide_on_grid_and_search is True, we don't want to show them,
      we only want to show products where hide_on_grid_and_search is False */
      {
        name: 'hide_on_grid_and_search',
        value: 'False',
      },
    ],
  };
};

// need to map these so that we can render the correct UI for the filters
// specifically for colors, sizes, and reviews
export const cioFacetsToUrlFilters = {
  tag_color_list: 'color',
  available_size_top: 'size_top',
  available_size_bottom: 'size_bottom',
  available_size_bra: 'size_bra',
  available_size_shoes: 'size_shoes',
  available_size_bralette: 'size_bralette',
  available_size_men: 'size_men',
  available_size_lingerie: 'size_lingerie',
  available_size_sport: 'size_sport',
};

// need to map these so that we can render the correct UI for the filters
// specifically for translated sizes
export const cioSizeFacetsToFilterPreferences = {
  available_size_top: 'top-size',
  available_size_bottom: 'bottom-size',
  available_size_bra: 'bra-size',
  available_size_shoes: 'shoe-size',
  available_size_bralette: 'bralette-size',
  available_size_men: 'men-size',
  available_size_lingerie: 'lingerie-size',
  available_size_sport: 'sport-size',
};

export const urlFiltersToCioFacets = {
  color: 'tag_color_list',
  size_top: 'available_size_top',
  size_bottom: 'available_size_bottom',
  size_bra: 'available_size_bra',
  size_shoes: 'available_size_shoes',
  size_bralette: 'available_size_bralette',
  size_men: 'available_size_men',
  size_lingerie: 'available_size_lingerie',
  size_sport: 'available_size_sport',
};

export const getCioSortOrder = (sortBy) => {
  return sortBy?.endsWith('_l2h') ? 'ascending' : 'descending';
};

export const getUrlFilters = ({ query, whitelistedCioFilters = [] }) => {
  const urlFilters = {};
  let urlSort = null;
  const urlIgnoredParams = {};

  for (const [key, value] of Object.entries(query)) {
    if (whitelistedCioFilters?.includes(key)) {
      urlFilters[key] = value.split(',');
      continue;
    }

    if (key === 'sort') {
      urlSort = value;
      continue;
    }

    urlIgnoredParams[key] = value.split(',');
  }

  return { urlSort, urlFilters, urlIgnoredParams };
};

export const getSanitizedFilterInputsForCio = (filters) => {
  const sanitizedFilters = removeCioEmptyFilters(filters);
  const transformedFilters = Object.keys(sanitizedFilters).reduce(
    (acc, key) => {
      const cioFacet = urlFiltersToCioFacets[key] || key;
      acc[cioFacet] = sanitizedFilters[key];
      return acc;
    },
    {}
  );
  return transformedFilters;
};

const getTotalFacetsToRender = (facets) => {
  let additionalFacets = 0;
  let sizeFacetEncountered = false;

  // Count additional facets needed for multiple size facets
  // We are doing this because later in the pipeline all size facets gets combined to 1, we want to ensure size facets + the rest = max facets
  for (const facet of facets) {
    if (facet.field.startsWith(SIZE_PREFIX)) {
      if (sizeFacetEncountered) {
        additionalFacets++; // Increment for every additional size facet
      } else {
        sizeFacetEncountered = true; // Mark the first size facet as encountered
      }
    }
  }
  // Calculate the total number of facets to render
  return MAX_FACET_COUNT + additionalFacets;
};

export const transformCioFacets = ({
  enableFacetLimit = false,
  facets,
  requestFilters = {},
  selectedSizeFilters = {},
  baseFilters = [],
  domain,
}) => {
  const facetBlacklist = config.get('public.brand.constructor.facetBlacklist');
  let whitelistedFacets;
  if (facetBlacklist) {
    whitelistedFacets = facets.reduce((acc, facet) => {
      if (!facetBlacklist.includes(facet.name)) {
        acc.push(facet);
      }
      return acc;
    }, []);
  }

  const transformedFacets = whitelistedFacets?.map((facet) => {
    const { display_name: displayName, options, name } = facet;
    const transformedName = cioFacetsToUrlFilters[name] || name;
    const categoryName = cioSizeFacetsToFilterPreferences[name];

    return {
      field: transformedName,
      label: displayName,
      totalAggregationCount: 50,
      items:
        options?.map((option) => {
          // if the size display hasn't been overwritten in Constructor, translate it
          let translatedDisplayName;
          if (categoryName && option.display_name === option.value) {
            translatedDisplayName = sizeByKey({
              domain,
              gender: 'F',
              category: categoryName,
              key: option.value,
            });
          }
          const displayName = getBrandValue({
            savage: translatedDisplayName || option.display_name,
            default: option.display_name,
          });

          return {
            backgroundCount: option.count,
            filterCount: option.count,
            value: option.value,
            label: displayName,
          };
        }) || [],
    };
  });

  if (!transformedFacets) {
    return [];
  }

  /**
   * Due to complexities related to size filter being handled in preFilterExpressions,
   * we may hit an edge case where
   * 1. a size filter (e.g. size_top) is selected by the user
   * 1. the size filter (e.g. size_top) is still shown in the CIO facet response and thus present in the Filters UI
   * 3. a non-size filter (e.g. compression, which is only for pants) that has no relation to the selected size filter is selected
   * 4. the size filter (e.g. size_top) will not be present in the CIO facet response and thus not present in the Filters UI
   *
   * ProductBrowser expects the selected sizeFilter (size_top) to be part of the aggregationFilters. This affects the
   * product browser state and puts it into a infinite loading state. Because size filters are now separate in preFilterExpressions,
   * there is no guarantee that the selected size filters will be returned in the CIO facet response. We need to resolve this by including
   * the selected size filters in the aggregationFilters.
   *
   * Caveat: we don't have access to the full list of size filters options in the CIO response, so we need to manually add them
   * by parsing the previous payload aggregationFilters. The result set will only include the selected filter options if it's not
   * part of the CIO facet response and will have a disabled state in the UI until the selected filter (e.g. compression) that caused
   * this edge case is deselected.
   *
   * Followup Note: refreshing the page or navigating directly to this page with the combination of conflicting filters (size_top and
   * commpression) will not cause the infinite loading state. The size_top filter will remain in the url but removed from the Filters UI
   * and removed from the aggregationFilters. This because the issue is only related to the loadItems() call and updates to the aggregationFilters
   * in the ProductBrowser component. We have a similar but separate flow for the initial load of the page in ConstructorSearch and BuilderConstructorGrid.
   */
  const sizeFacets = [];
  Object.entries(selectedSizeFilters).forEach(([key, value]) => {
    if (transformedFacets.find((facet) => facet.field === key)) {
      return;
    }

    if (!Array.isArray(value) || value.length === 0) {
      return;
    }

    const labelFromBaseFilters = baseFilters.find(
      (filter) => filter.field === key
    )?.label;

    sizeFacets.push({
      field: key,
      label: labelFromBaseFilters,
      totalAggregationCount: 50,
      items: value.map((size) => ({
        backgroundCount: 1,
        filterCount: 1,
        value: size,
        label: size,
      })),
    });
  });

  // Make sure size facets are first so that they don't get filtered out by slicing logic below
  const outputFacets = [...sizeFacets, ...transformedFacets];

  if (!enableFacetLimit) {
    return outputFacets;
  }

  const transformedRequestFilters = Object.keys(requestFilters)?.map(
    (filter) => cioFacetsToUrlFilters[filter] || filter
  );

  const transformedSizeFilters = Object.keys(selectedSizeFilters)?.map(
    (filter) => filter
  );

  // Ensure that facets that are selected are prioritized to not get sliced off
  // by the totalFacetsToRender limit.
  // Filter out facets with 1 or fewer options, unless the facet is included in
  // the request filters/has been selected.
  // e.g. keep the facets that have already been selected, even if there is
  // only one option for that facet
  // we need to keep already selected facets for ProductBrowser aggregations
  // and filterSettings to work properly otherwise, we run into potential infinite loops
  //
  // Note: the order of facets will be modified, facets that are marked as "forced"
  // in the constructor.io dashboard and set to be rendered in a given position
  // could be rendered lower than that position if they are not selected, but they
  // are still guaranteed to be rendered as non-"forced" facets will be pushed out
  // of the totalFacetsToRender limit first.
  const selectedFacets = [];
  const extraFacets = [];
  outputFacets.forEach((facet) => {
    if (
      transformedRequestFilters.includes(facet.field) ||
      transformedSizeFilters.includes(facet.field)
    ) {
      selectedFacets.push(facet);
    } else if (facet.items.length > 1) {
      extraFacets.push(facet);
    }
  });
  const filteredFacets = selectedFacets.concat(extraFacets);

  const totalFacetsToRender = getTotalFacetsToRender(filteredFacets);

  if (selectedFacets.length > totalFacetsToRender) {
    // ensurance policy, this shouldn't be able to happen unless someone manipulates
    // their url params to add more facets, but in case they do we don't want the grid
    // to break
    return selectedFacets;
  }
  return filteredFacets.slice(0, totalFacetsToRender);
};

export const transformAssetContainer = ({ assetContainer }) => {
  return [
    {
      container: assetContainer?.gridAssetContainer,
      mobile_container: assetContainer?.gridAssetContainerMobile,
    },
  ];
};

export const transformCioSortOptions = ({
  sortOptions = [],
  isVip,
  defaultSort = 'relevance',
}) => {
  return sortOptions
    ?.filter((option) => {
      if (option.sort_by?.startsWith('default_')) {
        return isVip;
      }

      if (option.sort_by?.startsWith('retail_')) {
        return !isVip;
      }

      return true;
    })
    ?.map((option) => {
      return {
        value: option.sort_by,
        sort: option.sort_by,
        defaultMessage: option.display_name,
        ...(option.sort_by === defaultSort && { isDefault: true }),
        ...(option.status === 'selected' && { selected: true }),
      };
    });
};

/**
 * This function maps the CIO sort options to match the user set builder sort options. Since the sort otpions in builder are
 * static and doesn't change often, where as builder gives site merch more control and allow them to choose
 * which sort options to display and drag the order around.
 * @param {Object} params - The input parameters.
 * @param {Array} params.builderSortOptions - An array of sort options provided by the builder, using human-readable keys.
 * @param {Array} params.sortOptions - An array of available CIO sort options.
 * @returns {Array} - An array of CIO sort options that match the builder-provided sort options.
 */
export function getCioSortOptionValuesFromBuilderSortOptions({
  builderSortOptions,
  sortOptions,
}) {
  if (!builderSortOptions || builderSortOptions.length === 0) {
    return sortOptions;
  }

  const builderSnakeCaseValues = builderSortOptions
    .map((option) => SortOptionsHumanToSnakeMapping[option.sortOption])
    .filter(Boolean);

  const finalOptions = builderSnakeCaseValues
    .map((snakeCaseValue) =>
      sortOptions.find((cioOption) => cioOption.value === snakeCaseValue)
    )
    .filter(Boolean);

  // If no matches, return all sortOptions
  return finalOptions.length > 0 ? finalOptions : sortOptions;
}

const camelCaseString = (str) => {
  return str
    .toLowerCase()
    .split('_')
    .map((word, index) =>
      index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join('');
};

/**
 * Transforms the keys and values of an object to camelCase
 * using `buildProductImagesFromImageService`.
 * @param {object|any[]} obj - The object or array to be transformed.
 * @returns {object|any[]} - The new object or array with camelCase keys and transformed values.
 */
export const transformObjectKeysAndValues = (obj) => {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => transformObjectKeysAndValues(item));
  }

  const newObj = {};
  Object.keys(obj).forEach((key) => {
    const newKey = camelCaseString(key);
    newObj[newKey] = transformObjectKeysAndValues(obj[key]);
  });
  return newObj;
};

/**
 * Extracts the effective SKU from a permalink.
 * - If the final segment is all numbers, the SKU is the second-to-last segment and the last segment, separated by a hyphen
 * - If the final segment contains both letters and numbers, the SKU is the final segment
 *
 * @param {string} permalink - The permalink value
 * @returns {string} The effective SKU extracted from the input.
 */
export function getEffectiveSkuFromPermalink({ permalink }) {
  if (!permalink) {
    return '';
  }

  // Split by hyphens
  const segments = permalink.split('-');

  // Need at least one segment
  if (segments.length === 0) {
    return '';
  }

  // Check the last segment
  const lastSegment = segments[segments.length - 1];

  // If last segment is numeric only
  if (lastSegment && /^\d+$/.test(lastSegment)) {
    // use the second-to-last segment and the last segment, separated by a hyphen
    if (segments.length >= 2) {
      return `${segments[segments.length - 2]}-${lastSegment}`;
    }
  } else if (lastSegment && /[a-z].*\d|\d.*[a-z]/i.test(lastSegment)) {
    // Last segment has mix of letters and numbers
    return lastSegment;
  }

  // Fallback
  return '';
}

/**
 * Constructs a URL for a product swatch image based on the SKU or permalink.
 * If a SKU is provided, it is used directly. Otherwise, it attempts to extract
 * the SKU from the permalink using the getEffectiveSkuFromPermalink function.
 * The URL format varies depending on the brand (Savage vs Fabletics).
 *
 * @param {string} sku - The stock keeping unit (SKU) for the product (optional if permalink is provided).
 * @param {string} permalink - The product permalink, used to extract SKU if not directly provided (optional if sku is provided).
 * @returns {string} The complete URL to the product's swatch image.
 */
export function buildSwatchImageUrl({ sku, permalink }) {
  // the effectiveSku is just the sku or the sku extracted from the permalink
  const effectiveSku = sku || getEffectiveSkuFromPermalink({ permalink });
  const cdnUrl = config.get('public.cdn');

  return getBrandValue({
    savage: `${cdnUrl}/media/images/products/${effectiveSku}/${effectiveSku}-SWATCH.jpg`,
    default: `${cdnUrl}/media/images/products/${effectiveSku}/${effectiveSku}-swatch_1_36x36.jpg`,
  });
}

// Helper function to check if the image URL will actually load
export async function checkImageUrl(url) {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    return false;
  }
}

function createImageUrlDefault({ baseUrl, sku, angle, size, plus = false }) {
  const plusSuffix = plus ? '-plus' : '';
  return `${baseUrl}${sku}/${sku}-${angle}_${size.width}x${size.height}${plusSuffix}.jpg`;
}

function createImageUrlSxf({ baseUrl, sku, angle, size, permalink }) {
  // Extract SKU from permalink if sku is missing
  const effectiveSku = sku || getEffectiveSkuFromPermalink({ permalink });

  if (effectiveSku && permalink) {
    // Format: baseUrl/SKU/PERMALINK-angle-widthxheight.jpg
    return `${baseUrl}${effectiveSku.toUpperCase()}/${permalink.toUpperCase()}-${angle}-${
      size.width
    }x${size.height}.jpg`;
  }
  return null;
}

// fallback images have just 1 angle (no seperate hover image) and does not distinguish between default and plus
function createFallbackImageUrl({ baseUrl, size }) {
  return `${baseUrl}image_service/no_image_returned_${size.width}x${size.height}.png`;
}

/**
 * Constructs URLs for product images based on options with optional plus sizes and fallbacks.
 * @param {Object} options - Configuration object containing SKU and whether to use fallback images.
 * @returns {Object} - Object containing arrays of image URLs grouped by angle and size.
 */
export async function buildStaticProductImages({
  sku,
  permalink,
  shouldUseFallback = false,
}) {
  const useSavagePlusSizeFallback = config.get(
    'public.brand.features.useSavagePlusSizeFallback'
  );
  const baseUrl = `${config.get('public.cdn')}/media/images/products/`;
  const createImageUrl = getBrandValue({
    default: createImageUrlDefault,
    savage: createImageUrlSxf,
  });
  const angles = getBrandValue({
    savage: [1, 'LAYDOWN'],
    default: [1, 2],
  });

  const images = {
    default: [],
    plus: [],
  };

  for (const angle of angles) {
    const angleImages = {
      angle,
      sizes: [],
    };
    const anglePlusImages = {
      angle,
      sizes: [],
    };

    // The fallback sizes are larger than what we currently use in Production.
    // Fallback Sizes: 1498x998 for desktop and 866x577 for mobile
    // Production Sizes: 866x577for desktop and 491x327 for mobile
    // TODO: we should standardize the sizes in the clean up ticket: https://techstyle-prod.atlassian.net/browse/FLD-2120
    const sizes = shouldUseFallback
      ? [
          { height: 1498, width: 998 },
          { height: 866, width: 577 },
        ]
      : getBrandValue({
          default: [
            { height: 491, width: 327 },
            { height: 866, width: 577 },
          ],
          savage: [{ height: 800, width: 600 }],
        });

    for (const size of sizes) {
      const imageUrl = shouldUseFallback
        ? createFallbackImageUrl({ baseUrl, size })
        : createImageUrl({ baseUrl, sku, angle, size, permalink, plus: false });

      angleImages.sizes.push({
        height: size.height,
        width: size.width,
        url: imageUrl,
      });

      // Skip plus-size image check for Savage brand since they don't have plus-size images
      if (!shouldUseFallback && !useSavagePlusSizeFallback) {
        const plusImageUrl = createImageUrl({
          baseUrl,
          sku,
          angle,
          size,
          permalink,
          plus: true,
        });
        // Always check for plus-size images when not in Savage mode
        const plusImageExists = await checkImageUrl(plusImageUrl);
        if (plusImageExists) {
          anglePlusImages.sizes.push({
            height: size.height,
            width: size.width,
            url: plusImageUrl,
          });
        }
      }
    }

    images.default.push(angleImages);
    if (anglePlusImages.sizes.length > 0) {
      images.plus.push(anglePlusImages);
    }
  }

  // Ensure 'plus' array is always present even if it's empty
  if (images.plus.length === 0) {
    images.plus = [];
  }

  return images;
}

/**
 * Retrieves product images from an image service or falls back to a default image URL.
 * @param {Object} options - Object containing the imageServiceUrl and imageUrlFromProduct.
 * @param {string} options.imageServiceUrl - URL of the image service.
 * @param {string} options.itemNumber - or use the product's item number to construct the image service URL.
 * @param {string} options.imageUrlFromProduct - Fallback URL from the product data.
 * @returns {Object|boolean} - Object containing URLs for default and plus model images, or false if no valid URLs are found.
 */
export function buildProductImagesFromImageService({
  imageServiceUrl,
  itemNumber,
  imageUrlFromProduct,
  cacheHoverImage = true,
}) {
  const isImageServiceUrlValid =
    typeof imageServiceUrl === 'string' && imageServiceUrl.trim() !== '';
  const isImageUrlFromProductValid =
    typeof imageUrlFromProduct === 'string' &&
    imageUrlFromProduct.trim() !== '';

  // return false if neither imageServiceUrl nor imageUrlFromProduct is valid
  if (!isImageServiceUrlValid && !isImageUrlFromProductValid && !itemNumber) {
    return false;
  }

  const useImageService = isImageServiceUrlValid || itemNumber;

  const sizes = getBrandValue({
    savage: [{ height: 800, width: 600, sizeParam: '600x800' }],
    default: [
      { height: 491, width: 327, sizeParam: '327x491' },
      { height: 866, width: 577, sizeParam: '577x866' },
    ],
  });
  const angles = [1, 2];
  const models = ['d', 'p']; // d = default, p = plus
  const images = {};
  const preloadImageUrls = [];

  for (const model of models) {
    const modelImages = [];
    for (const angle of angles) {
      const angleModelImages = {
        angle,
        sizes: [],
      };

      for (const size of sizes) {
        const imageUrl = useImageService
          ? generateImageServiceImageUrl({
              imageServiceUrl,
              product: {
                itemNumber,
              },
              model,
              angle,
              height: size.height,
              width: size.width,
            })
          : imageUrlFromProduct;

        angleModelImages.sizes.push({
          height: size.height,
          width: size.width,
          url: imageUrl,
        });

        if (angle === 2 && cacheHoverImage) {
          preloadImageUrls.push(imageUrl);
        }
      }

      modelImages.push(angleModelImages);
    }
    images[model === 'd' ? 'default' : 'plus'] = modelImages;
  }

  // Preload images into the browser cache using the JavaScript Image object.
  preloadImageUrls.forEach((url) => {
    const img = new Image();
    img.src = url;
  });

  return images;
}

/**
 * Creates an image service url for related products and fetches the complete product images
 * using `buildProductImagesFromImageService`.
 * @param {Object} relatedProducts - The object containing a map of products and their related products.
 * @returns {Object} - Returns the updated `relatedProductsMap` object with product images.
 */
export const setImageServiceForRelatedProducts = ({ relatedProductsMap }) => {
  if (relatedProductsMap === undefined) {
    return {};
  }

  if (typeof relatedProductsMap !== 'object' || relatedProductsMap === null) {
    debug('relatedProductsMap is not an object');
    return {};
  }

  Object.keys(relatedProductsMap).flatMap((key) => {
    const productList =
      relatedProductsMap[key]?.relatedProductIdObjectList || [];

    return productList.map((product) => {
      if (!product?.itemNumber) {
        return product;
      }

      const productImages = buildProductImagesFromImageService({
        itemNumber: product.itemNumber,
        cacheHoverImage: false,
      });

      // Update productImages with the new images from the image service if available
      if (productImages) {
        product.productImages = productImages;
      }

      return product;
    });
  });

  return relatedProductsMap;
};

/**
 * Transforms Constructor IO data to the V2 format used in our grids
 * @param {Object} params.data - Data received from Constructor IO.
 * @param {boolean} params.shouldUseImageService - Feature flag indicating whether to use the image service.
 * @returns {Object} - The transformed data including product images and swatch URLs.
 */
export const parseCioDataToV2 = async ({ data, shouldUseImageService }) => {
  const formattedData = transformObjectKeysAndValues(data);
  const swatchUrl = buildSwatchImageUrl({
    sku: formattedData.sku,
    permalink: formattedData.permalink,
  });

  let productImages = {};

  // TODO: clean up this logic and remove feature flag once image service has been validated.
  // JIRA ticket: https://techstyle-prod.atlassian.net/browse/FLD-2120
  if (shouldUseImageService) {
    productImages = buildProductImagesFromImageService({
      imageServiceUrl: formattedData.imageServiceUrl,
      imageUrlFromProduct: formattedData.imageUrl,
    });
    // if the image service function did not return valid URLs, fall back to default static CDN URLs
    if (!productImages) {
      productImages = await buildStaticProductImages({
        shouldUseFallback: true,
      });
    }
  } else {
    // if the feature flag is off, use the static CDN URLs built from sku
    productImages = await buildStaticProductImages({
      sku: formattedData.sku,
      permalink: formattedData.permalink,
    });
  }

  const masterProduct = Number(formattedData.id);
  if (!isNaN(masterProduct)) {
    formattedData.masterProductId = masterProduct;
  }

  return {
    ...formattedData,
    swatchUrl,
    productImages,
  };
};

export const transformCioResults = async ({
  results,
  shouldUseImageService = false,
}) => {
  const promises = results?.map(async (result) => {
    const formattedData = await parseCioDataToV2({
      data: result?.data,
      shouldUseImageService,
    });

    return {
      ...formattedData,
    };
  });

  return Promise.all(promises); // Wait for all promises to resolve
};

export const removeCioEmptyFilters = (obj) => {
  return Object.entries(obj).reduce((entry, [key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      entry[key] = value;
    }
    return entry;
  }, {});
};

/**
 * Base filtering method for both trending and featured products.
 * Generates a filter object for querying products based on user membership status (Elite/VIP),
 * the current date, and whether to include warehouse products.
 *
 * @param {boolean} isElite - Indicates if the user is an elite member.
 * @param {boolean} isVip - Indicates if the user is a VIP member.
 * @param {boolean} shouldShowWarehouseProducts - Controls whether warehouse products are included in the autosuggest.
 * @returns {object} - A filter object with membership exclusions and date/product type conditions.
 */
const getBaseProductsFilters = ({
  isElite,
  isVip,
  shouldShowWarehouseProducts,
}) => {
  const useSavageCIOFilters = config.get(
    'public.brand.features.useSavageCIOFilters'
  );
  if (useSavageCIOFilters) {
    return getSXFProductsFilters({
      isElite,
      isVip,
      shouldShowWarehouseProducts,
    });
  }

  return getFLProductsFilters({
    isElite,
    isVip,
    shouldShowWarehouseProducts,
  });
};

const getSXFProductsFilters = ({ isElite, isVip }) => {
  const staticCurrentDate = new Date();
  const dateNowInMs = staticCurrentDate.getTime();
  const membershipExclusionFilters = getMembershipExclusionFilters({
    isElite,
    isVip,
  });
  return {
    /* constructor syntax to filter for all products expected to be released from the dawn of time until now,
    see https://constructor-io.github.io/constructorio-client-javascript/module-autocomplete.html#~getAutocompleteResults */
    date_expected_epoch_timestamp: `-inf-${dateNowInMs}`,
    /* This is hard coded to False because the filter only returns products where the criteria is matched,
      for products where hide_on_grid_and_search is True, we don't want to show them,
      we only want to show products where hide_on_grid_and_search is False */
    hide_on_grid_and_search: 'False',
    ...membershipExclusionFilters.reduce((acc, filter) => {
      acc[filter.name] = filter.value;
      return acc;
    }, {}),
  };
};

const getFLProductsFilters = ({
  isElite,
  isVip,
  shouldShowWarehouseProducts = false,
}) => {
  const staticCurrentDate = new Date();
  const dateNowInMs = staticCurrentDate.getTime();
  const membershipExclusionFilters = getMembershipExclusionFilters({
    isElite,
    isVip,
  });
  return {
    /* constructor syntax to filter for all products expected to be released from the dawn of time until now,
    see https://constructor-io.github.io/constructorio-client-javascript/module-autocomplete.html#~getAutocompleteResults */
    date_expected_epoch_timestamp: `-inf-${dateNowInMs}`,
    is_warehouse_product: shouldShowWarehouseProducts ? 'True' : 'False',
    /* This is hard coded to False because the filter only returns products where the criteria is matched,
      for products where hide_on_grid_and_search is True, we don't want to show them,
      we only want to show products where hide_on_grid_and_search is False */
    hide_on_grid_and_search: 'False',
    ...membershipExclusionFilters.reduce((acc, filter) => {
      acc[filter.name] = filter.value;
      return acc;
    }, {}),
  };
};

/**
 * Generates a filter object for trending products (recommendations) from Constructor.io
 * Has extra filtering based on logged in user and gender.
 *
 * @param {boolean} isElite - Indicates if the user is an elite member.
 * @param {boolean} isVip - Indicates if the user is a VIP member.
 * @param {boolean} shouldShowWarehouseProducts - Controls whether warehouse products are included in the autosuggest.
 * @param {boolean} isLoggedIn - If user is currently logged in
 * @param {string} gender - either 'F' or 'M', used to filter products based on gender
 * @returns {object} - A filter object with membership exclusions and date/product type conditions.
 */
export const getTrendingProductsFilters = ({
  isElite,
  isVip,
  shouldShowWarehouseProducts = false,
  isLoggedIn,
  gender,
  isScrubs,
}) => {
  const extraFilters = {};
  const useFableticsGenderFilters = config.get(
    'public.brand.features.useFableticsGenderFilters'
  );

  // fabletics is the only brand that has men and women accounts
  if (useFableticsGenderFilters) {
    if (isLoggedIn) {
      if (gender === 'M') {
        // only show fabletics/scrubs for men
        extraFilters.membership_brand_id =
          MEMBERSHIP_BRAND_ID_ENUMS.FABLETICS.toString();
        extraFilters.gender = 'male';
      }
      if (gender === 'F') {
        // for women on the fab site, show any product from fab/yit/scrubs
        extraFilters.gender = 'female';
      }
    }
    if (isScrubs) {
      extraFilters.product_category_label = 'Scrubs';
    }
  }

  return {
    ...getBaseProductsFilters({ isElite, isVip, shouldShowWarehouseProducts }),
    ...extraFilters,
  };
};

/**
 * Generates a filter object for suggested products (autocomplete) from Constructor.io.
 *
 * @param {boolean} isElite - Indicates if the user is an elite member.
 * @param {boolean} isVip - Indicates if the user is a VIP member.
 * @param {boolean} shouldShowWarehouseProducts - Controls whether warehouse products are included in the autosuggest.
 * @returns {object} - A filter object with membership exclusions and date/product type conditions.
 */
export const getSuggestedProductsFilters = ({
  isElite,
  isVip,
  shouldShowWarehouseProducts = false,
}) => {
  const extraFilters = {};

  return {
    ...getBaseProductsFilters({ isElite, isVip, shouldShowWarehouseProducts }),
    ...extraFilters,
  };
};

/**
 * Translates filter tags by replacing the text content of elements with localized translations.
 * @param {Object} translationDict - A dictionary object where keys are original text and values are the translated text.
 * @param {string} querySelector - The CSS selector used to target the elements that need to be translated.
 */
export const translateFilterTags = ({ translationDict, querySelector }) => {
  // Select all elements matching the given selector
  const elements = document.querySelectorAll(querySelector);

  elements.forEach((element) => {
    const text = element.textContent.trim();
    // Replace text content if it's in the dictionary
    if (translationDict[text]) {
      element.textContent = translationDict[text];
    }
  });
};

/**
 * Updates the localized label of a specific DOM element based on the locale.
 * @param {string} querySelector - The CSS selector used to target the element whose label needs to be updated.
 * @param {string} localizationKey - The key used to retrieve the localized label from the localization dictionary.
 * @param {string} locale - The current locale (e.g., "en-US", "de-DE") to determine the appropriate translation.
 */
export const updateLocalizedLabel = ({
  querySelector,
  localizationKey,
  locale,
}) => {
  const getLocalizedLabel = (labelKey) => {
    const localizedLabel = LOCALIZED_LABELS[labelKey];
    return localizedLabel?.[locale] || localizedLabel?.['en-US'];
  };

  const element = document.querySelector(querySelector);
  if (element) {
    element.innerText = getLocalizedLabel(localizationKey);
  }
};

/**
 * Determines the initial filters to be automatically applied to the cio grid based on the user's profile size and gender.
 *
 * @param {Object} params.initialFilters - The initial set of filters from the url to be potentially modified.
 * @param {Object} params.profileSizes - User's profile sizes.
 * @param {Object} params.automaticSizeConfig - Configuration object containing:
 *        @param {boolean} params.automaticSizeConfig.builderAutomaticSizeFiltering - Boolean indicating if automatic filtering based on builder is enabled.
 *        @param {string} params.automaticSizeConfig.customerGender - The gender of the current user.
 * @returns {Object} An object containing the filters that were automatically applied, keyed by filter category.
 */
export const applyAutoFilters = ({
  initialFilters,
  profileSizes,
  builderAutomaticSizeFiltering,
  gender,
}) => {
  // Clone the initialFilters to avoid mutating the original object
  const mergedFilters = JSON.parse(JSON.stringify(initialFilters));

  const { autoAppliedFilters } = getAutoAppliedInitialFilters({
    initialFilters,
    profileSizes,
    automaticSizeConfig: {
      builderAutomaticSizeFiltering,
      customerGender: gender,
    },
  });

  if (autoAppliedFilters) {
    // Iterate through the keys of autoAppliedFilters
    Object.keys(autoAppliedFilters).forEach((key) => {
      // If the key exists in aggregationFilter, merge the values
      if (
        Object.prototype.hasOwnProperty.call(
          mergedFilters.aggregationFilter,
          key
        )
      ) {
        mergedFilters.aggregationFilter[key] = autoAppliedFilters[key];
      }
    });
  }

  return mergedFilters;
};

/**
 * @description Translates an array of sort options from human-readable format to snake_case format.
 *
 * @param {Object[]} sortOptions - An array of objects that contain a sortOption string that will be translated.
 */
export function translateSortOptions(sortOptions) {
  if (!Array.isArray(sortOptions)) {
    return [];
  }

  return sortOptions
    .map((option) => SortOptionsHumanToSnakeMapping[option.sortOption])
    .filter(Boolean);
}

export const normalizeFplCodes = (fplCodes) =>
  fplCodes.map((code) => `{'featured_product_location_code': '${code}'}`);
